import { algoliasearch, SearchClient } from "algoliasearch"; // Import from your Algolia setup

// Types for filter operations
type FilterOperator = "==" | "===" | "!=" | "<" | "<=" | ">" | ">=" | "in";

interface AlgoliaFilter {
  field: string;
  operator: FilterOperator;
  value: string | number | boolean | string[] | number[];
}

interface AlgoliaHit {
  objectID: string;
  [key: string]: any;
}

interface AlgoliaSearchParams {
  query: string;
  hitsPerPage: number;
  filters?: string;
}

interface AlgoliaSearchResponse {
  hits: AlgoliaHit[];
}

// Validate environment variables
const appId = process.env.REACT_APP_ALGOLIA_APP_ID;
const searchKey =
  process.env.REACT_APP_ALGOLIA_SEARCH_KEY ||
  process.env.REACT_APP_ALGOLIA_SEARCH;

if (!appId || !searchKey) {
  console.error("Missing Algolia environment variables:", {
    appId: !!appId,
    searchKey: !!searchKey,
  });
  throw new Error(
    "REACT_APP_ALGOLIA_APP_ID and REACT_APP_ALGOLIA_SEARCH_KEY must be set",
  );
}

const searchClient = algoliasearch(appId, searchKey);

/**
 * Handle search query with Algolia
 */
export const handleQuerySearch = async (
  searchTerm: string,
  setAlgoliaResults: (results: AlgoliaHit[]) => void,
  setSearchValue: (value: string) => void,
  collection: string,
  filters: AlgoliaFilter[],
  customFilter: string | null,
): Promise<void> => {
  console.log("handleQuerySearch", {
    searchTerm,
    collection,
    filters,
    customFilter,
  });
  try {
    const results = await queryCollectionAlgolia(
      collection, // indexName
      filters, // filtersArr (only used if customFilter is null)
      searchTerm, // searchTerm
      50, // limit
      customFilter, // customFilterOverride
    );

    setAlgoliaResults(results);
    setSearchValue(searchTerm);
  } catch (error) {
    console.error("Error in handleQuerySearch:", error);
    setAlgoliaResults([]);
  }
};

/**
 * Query an Algolia index with filters + search term (V5 compatible)
 * @param indexName - Name of the Algolia index (e.g., "folder")
 * @param filtersArr - Array of filter objects [{ field, operator, value }]
 * @param searchTerm - The text to search for ("" means no term)
 * @param limit - Max hits to return
 * @param overrideAlgoliaFilters - Custom filter string to override filtersArr
 * @returns Array of Algolia hits
 */
export async function queryCollectionAlgolia(
  indexName: string,
  filtersArr: AlgoliaFilter[] = [],
  searchTerm: string = "",
  limit: number = 50,
  overrideAlgoliaFilters: string | null = null,
): Promise<AlgoliaHit[]> {
  console.log("queryCollectionAlgolia");

  if (!indexName) {
    console.error("Index name is missing");
    return [];
  }

  // Convert filters array into Algolia filter string
  let filtersStr = "";

  if (filtersArr && filtersArr.length > 0) {
    filtersStr = filtersArr
      .map((f) => {
        const { field, operator, value } = f;

        if (operator === "==" || operator === "===") {
          // Values with spaces MUST be quoted
          if (typeof value === "string" && value.includes(" ")) {
            const escapedValue = value.replace(/"/g, '\\"');
            return `${field}:"${escapedValue}"`;
          } else {
            // Simple values without spaces don't need quotes
            return `${field}:${value}`;
          }
        } else if (["<", "<=", ">", ">="].includes(operator)) {
          return `${field}${operator}${value}`;
        } else if (operator === "!=") {
          const escapedValue =
            typeof value === "string" ? value.replace(/"/g, '\\"') : value;
          return `NOT ${field}:"${escapedValue}"`;
        } else if (operator === "in" && Array.isArray(value)) {
          const orFilters = value.map((v) => {
            const escapedValue =
              typeof v === "string" ? v.replace(/"/g, '\\"') : v;
            return `${field}:"${escapedValue}"`;
          });
          return `(${orFilters.join(" OR ")})`;
        }

        console.warn(`Unsupported operator: ${operator}`);
        return "";
      })
      .filter(Boolean)
      .join(" AND ");
  }

  if (overrideAlgoliaFilters) {
    filtersStr = overrideAlgoliaFilters;
  }

  // V5: Build search parameters object
  const searchParams: AlgoliaSearchParams = {
    query: searchTerm || "",
    hitsPerPage: limit,
  };

  if (filtersStr) {
    searchParams.filters = filtersStr;
  }

  console.log("searchParams", searchParams);

  // Handle sorting - use different index name for sorting
  const targetIndex = indexName;

  try {
    // V5: Use searchSingleIndex method instead of initIndex
    // Note: You'll need to import and initialize searchClient properly
    const { hits }: AlgoliaSearchResponse =
      await searchClient.searchSingleIndex({
        indexName: targetIndex,
        searchParams,
      });

    return hits || [];
  } catch (error) {
    console.error("Error querying Algolia:", error);

    // Always try fallback without sorting if there's any error
    console.warn("Trying search without sorting...");
    try {
      const fallbackParams: AlgoliaSearchParams = {
        query: searchTerm || "",
        hitsPerPage: limit,
      };

      if (filtersStr) {
        fallbackParams.filters = filtersStr;
      }

      const { hits }: AlgoliaSearchResponse =
        await searchClient.searchSingleIndex({
          indexName: indexName, // Use original index name
          searchParams: fallbackParams,
        });

      return hits || [];
    } catch (fallbackError) {
      console.error("Fallback query also failed:", fallbackError);
      return [];
    }
  }
}
